import { Typography } from '@/components';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import styles from './Select.module.css';

interface SelectProps<T> {
  options: T[];
  initialOption?: T;
  placeholder?: string;
  onChange?: (selected: T | undefined) => void;
  getOptionLabel: (option: T) => string;
  getOptionKey: (option: T) => string | number;
}

const Select = <T,>({
  options,
  initialOption,
  placeholder,
  onChange,
  getOptionLabel,
  getOptionKey,
}: SelectProps<T>): JSX.Element => {
  const [selectedOption, setSelectedOption] = useState<T | undefined>(
    initialOption,
  );
  const [isOpen, setIsOpen] = useState(false);
  const toggleContainerRef = useRef<HTMLDivElement>(null);

  const handleOption = (option: T) => {
    setSelectedOption(option);
    setIsOpen(false);
  };

  const handleOutsideClick = (event: MouseEvent) => {
    if (
      toggleContainerRef.current &&
      !toggleContainerRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    } else {
      document.removeEventListener('mousedown', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isOpen]);

  useEffect(() => {
    if (onChange) {
      onChange(selectedOption);
    }
  }, [selectedOption]);

  useEffect(() => {
    if (selectedOption && !options.includes(selectedOption)) {
      setSelectedOption(undefined);
    }
  }, [options]);

  return (
    <div
      ref={toggleContainerRef}
      className={classNames(styles.container, { [styles.opened]: isOpen })}
      onClick={() => setIsOpen((previous) => !previous)}
    >
      <div className={styles.inputArea}>
        {selectedOption ? (
          <span className={styles.value}>{getOptionLabel(selectedOption)}</span>
        ) : (
          <span className={styles.placeholder}>{placeholder}</span>
        )}
      </div>
      {isOpen && (
        <div className={styles.dropdown}>
          {options.map((option) => (
            <div
              key={getOptionKey(option)}
              className={styles.option}
              onClick={(event) => {
                event.stopPropagation();
                handleOption(option);
              }}
            >
              <Typography variant="bodyMedium">
                {getOptionLabel(option)}
              </Typography>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Select;
